import React, { useMemo, useRef, useState } from 'react'
import { Dialog, DialogContent, TextField, Button, IconButton, CircularProgress } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Toast } from 'components/shared/Toaster'
import strings from 'strings'
import { reassignTask } from 'actions/collaborativeTasks'
import { useDirectorySuggestions } from '../hooks/useDirectorySuggestions'
import { isEmailFormatValid } from '../ctHelpers'

const ReassignTaskModal = ({ open, onClose, task, applicationUuid, onDone }) => {
    const t = strings.CT
    const [email, setEmail] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)
    const inFlight = useRef(false)

    const { suggestions, loading: suggestLoading } = useDirectorySuggestions(applicationUuid, email)

    const externalGuess = useMemo(() => {
        if (!isEmailFormatValid(email)) return false
        return !suggestions.some(s => s.email?.toLowerCase() === email.trim().toLowerCase())
    }, [email, suggestions])

    const handleClose = () => {
        setEmail(''); setError(null); setSubmitting(false)
        inFlight.current = false
        onClose()
    }

    const handlePickSuggestion = (s) => { setEmail(s.email) }

    const handleSubmit = async () => {
        if (!email.trim()) { setError(t.REQUIRED_ASSIGNEE); return }
        if (!isEmailFormatValid(email)) { setError(t.INVALID_EMAIL); return }
        if (inFlight.current) return
        inFlight.current = true
        setSubmitting(true)
        try {
            const res = await reassignTask(task.id, { assigneeEmail: email.trim() })()
            Toast({ type: 'success', message: t.REASSIGNED_OK })
            onDone && onDone(res?.response || res)
            handleClose()
        } catch (e) {
            Toast({ type: 'error', message: t.REASSIGNED_ERR })
        } finally {
            setSubmitting(false)
            inFlight.current = false
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth classes={{ paper: 'ct-modal-paper' }}>
            <DialogContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="ct-modal-title">{t.MODAL_REASSIGN_TITLE}</div>
                    <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
                </div>

                {task?.assignee && (
                    <>
                        <label className="ct-field-label">{t.CURRENT_ASSIGNEE}</label>
                        <div className="ct-suggestion-email">{task.assignee.email}</div>
                    </>
                )}

                <label className="ct-field-label ct-field-label__required">{t.FIELD_ASSIGN_TO}</label>
                <TextField
                    variant="standard"
                    fullWidth
                    placeholder={t.FIELD_ASSIGN_TO_PLACEHOLDER}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    error={!!error}
                    helperText={error}
                    InputProps={{
                        endAdornment: externalGuess
                            ? <span className="ct-ext-badge">{t.EXT_BADGE}</span>
                            : null
                    }}
                />

                {externalGuess && (
                    <div className="ct-ext-warning">
                        <InfoOutlinedIcon style={{ fontSize: 16, color: '#5B21B6', flexShrink: 0, marginTop: 1 }} />
                        <span>{t.EXT_WARNING}</span>
                    </div>
                )}

                {(suggestLoading || suggestions.length > 0) && (
                    <div className="ct-suggestions">
                        <div className="ct-field-label" style={{ marginTop: 0 }}>{t.SUGGESTIONS_HEADER}</div>
                        {suggestLoading && <CircularProgress size={16} style={{ color: '#7B61FF' }} />}
                        {!suggestLoading && suggestions.map(s => (
                            <div key={s.email} className="ct-suggestion-row" onClick={() => handlePickSuggestion(s)}>
                                <div>
                                    <div className="ct-suggestion-name">{s.name || s.email}</div>
                                    <div className="ct-suggestion-email">{s.email}</div>
                                </div>
                                {s.roleLabel && <div className="ct-suggestion-role">{s.roleLabel}</div>}
                            </div>
                        ))}
                    </div>
                )}

                <div className="ct-modal-footer">
                    <span className="ct-modal-footer__hint">{t.FOOTER_NOTIFICATION_HINT}</span>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Button onClick={handleClose} disabled={submitting}>{t.ACTION_CANCEL}</Button>
                        <Button
                            variant="contained"
                            className="ct-new-task-btn"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={14} style={{ color: '#fff' }} /> : null}
                        >
                            {t.ACTION_REASSIGN}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default ReassignTaskModal
